import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { KanbanColumn } from '@/components/Kanban/KanbanColumn';
import { KanbanCard, WorkOrderCard } from '@/components/Kanban/KanbanCard';
import {
  RefreshCw,
  Clock,
  CalendarDays,
  Play,
  Pause,
  CheckCircle,
  User,
  AlertTriangle,
  Kanban,
} from 'lucide-react';
import { ModuleTabs } from '@/components/ui/ModuleTabs';

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  quantity: number;
  priority: string;
  planning_status: string;
  is_paused: boolean;
  assigned_operator: string | null;
  mold_id: string | null;
  customer?: { company_name: string };
  operator?: { full_name: string };
}

interface Operator {
  id: string;
  operator_id: string;
  status: string;
  full_name: string;
}

const COLUMNS = [
  { id: 'standby', title: 'Backlog', icon: Clock, color: 'bg-gray-500' },
  { id: 'scheduled', title: 'Programadas', icon: CalendarDays, color: 'bg-blue-500' },
  { id: 'in_production', title: 'En Proceso', icon: Play, color: 'bg-green-500' },
  { id: 'paused', title: 'Pausadas', icon: Pause, color: 'bg-amber-500' },
  { id: 'completed', title: 'Completadas', icon: CheckCircle, color: 'bg-emerald-700' },
];

export default function ProductionKanban() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState<WorkOrder[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeOrder, setActiveOrder] = useState<WorkOrder | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const [requirementsDialogOpen, setRequirementsDialogOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{ order: WorkOrder; targetStatus: string } | null>(null);

  const canManage = userRole === 'admin' || userRole === 'supervisor';
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, operatorsRes] = await Promise.all([
        api.from('work_orders')
          .select('*, customer:customers(company_name), operator:profiles!work_orders_assigned_operator_fkey(full_name)')
          .eq('is_active', true)
          .in('planning_status', ['standby', 'scheduled', 'in_production', 'completed'])
          .order('priority', { ascending: false }),
        api.from('operator_workload').select('*, operator:profiles(full_name)').order('status'),
      ]);
      if (ordersRes.error) throw ordersRes.error;
      if (operatorsRes.error) throw operatorsRes.error;
      setOrders(ordersRes.data || []);
      setOperators((operatorsRes.data || []).map((op: any) => ({ ...op, full_name: op.operator?.full_name || 'Sin nombre' })));
    } catch (error: any) {
      toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 30000); return () => clearInterval(i); }, [fetchData]);

  const getOrdersByStatus = (status: string) => {
    if (status === 'paused') return orders.filter(o => o.is_paused);
    if (status === 'in_production') return orders.filter(o => o.planning_status === 'in_production' && !o.is_paused);
    return orders.filter(o => o.planning_status === status && !o.is_paused);
  };

  const handleDragStart = (e: DragStartEvent) => setActiveOrder(orders.find(o => o.id === e.active.id) || null);

  const validateRequirements = (order: WorkOrder): string[] => {
    const missing: string[] = [];
    if (!order.assigned_operator) missing.push('Operario no asignado');
    if (!order.mold_id) missing.push('Molde no asignado');
    return missing;
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    setActiveOrder(null);
    if (!over || !canManage) return;
    const order = orders.find(o => o.id === active.id);
    if (!order) return;
    const targetStatus = over.id as string;
    const currentStatus = order.is_paused ? 'paused' : order.planning_status;
    if (currentStatus === targetStatus) return;
    if (targetStatus === 'in_production') {
      const missing = validateRequirements(order);
      if (missing.length > 0) { setPendingMove({ order, targetStatus }); setRequirementsDialogOpen(true); return; }
    }
    await updateOrderStatus(order, targetStatus);
  };

  const updateOrderStatus = async (order: WorkOrder, targetStatus: string) => {
    try {
      const updates: any = {};
      if (targetStatus === 'paused') { updates.is_paused = true; updates.paused_at = new Date().toISOString(); }
      else { updates.planning_status = targetStatus; if (order.is_paused) { updates.is_paused = false; updates.paused_at = null; } }
      const { error } = await api.from('work_orders').update(updates).eq('id', order.id);
      if (error) throw error;
      toast({ title: 'OT actualizada' });
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleAssignOperator = async (operatorId: string) => {
    if (!selectedOrder) return;
    try {
      const { error } = await api.from('work_orders').update({ assigned_operator: operatorId }).eq('id', selectedOrder.id);
      if (error) throw error;
      toast({ title: 'Operario asignado' });
      setAssignDialogOpen(false);
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleCardClick = (order: WorkOrder) => { if (canManage) { setSelectedOrder(order); setAssignDialogOpen(true); } };
  const availableOperators = operators.filter(op => op.status === 'available');

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-[calc(100vh-100px)]"><RefreshCw className="w-8 h-8 animate-spin" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-80px)]">
        <div className="p-4 border-b">
          <ModuleTabs
            tabs={[
              { value: 'calendario', label: 'Calendario', href: '/schedule' },
              { value: 'planificador', label: 'Planificador', href: '/production-planner' },
              { value: 'gantt', label: 'Gantt', href: '/gantt-planner' },
              { value: 'kanban', label: 'Kanban', href: '/production-kanban' },
            { value: 'operarios', label: 'Operarios', href: '/operators-management' },
            ]}
          />
        </div>
        <div className="flex items-center justify-between p-4 border-b">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Kanban className="w-6 h-6" />Planificador Kanban</h1>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{orders.length} OTs</Badge>
            <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
          </div>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex-1 overflow-x-auto p-4">
            <div className="flex gap-4 h-full min-w-max">
              {COLUMNS.map(col => (
                <KanbanColumn key={col.id} id={col.id} title={col.title} icon={col.icon} color={col.color} count={getOrdersByStatus(col.id).length}>
                  {getOrdersByStatus(col.id).map(order => <KanbanCard key={order.id} order={order} onClick={() => handleCardClick(order)} />)}
                </KanbanColumn>
              ))}
            </div>
          </div>
          <DragOverlay>{activeOrder && <WorkOrderCard order={activeOrder} isDragging />}</DragOverlay>
        </DndContext>
        <div className="border-t bg-muted/30 p-3">
          <div className="flex items-center gap-4 overflow-x-auto">
            <span className="text-sm font-medium text-muted-foreground">OPERARIOS:</span>
            {operators.map(op => (
              <div key={op.id} className="flex items-center gap-2 px-3 py-1.5 bg-background rounded-full border">
                <div className={'w-2 h-2 rounded-full ' + (op.status === 'available' ? 'bg-green-500' : op.status === 'working' ? 'bg-blue-500' : 'bg-amber-500')} />
                <span className="text-sm">{op.full_name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Asignar Operario - {selectedOrder?.order_number}</DialogTitle></DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">Producto: {selectedOrder?.product_name}</p>
            {selectedOrder?.operator && <p className="text-sm mb-4">Actual: <strong>{selectedOrder.operator.full_name}</strong></p>}
            <div className="space-y-2">
              <p className="text-sm font-medium">Disponibles:</p>
              {availableOperators.length === 0 ? <p className="text-sm text-muted-foreground">No hay operarios disponibles</p> : availableOperators.map(op => (
                <Button key={op.operator_id} variant="outline" className="w-full justify-start" onClick={() => handleAssignOperator(op.operator_id)}><User className="w-4 h-4 mr-2" />{op.full_name}</Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={requirementsDialogOpen} onOpenChange={setRequirementsDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-5 h-5" />Requisitos Faltantes</DialogTitle></DialogHeader>
          <div className="py-4">
            <p className="text-sm mb-4">No se puede iniciar <strong>{pendingMove?.order.order_number}</strong>:</p>
            <ul className="list-disc list-inside">{pendingMove && validateRequirements(pendingMove.order).map((r, i) => <li key={i} className="text-red-600">{r}</li>)}</ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequirementsDialogOpen(false)}>Entendido</Button>
            {pendingMove && !pendingMove.order.assigned_operator && <Button onClick={() => { setRequirementsDialogOpen(false); setSelectedOrder(pendingMove.order); setAssignDialogOpen(true); }}>Asignar Operario</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
